import { Send } from "lucide-react";
import type { FC } from "react";
import { type ChangeEvent, type KeyboardEvent, useCallback, useEffect, useRef, useState, useMemo } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
// import { createMyWorker } from "brain-worker";

import { Button } from "@/lib/ChatComponents/button";
import { ChatBubble, ChatBubbleAvatar, ChatBubbleMessage } from "@/lib/ChatComponents/chat-bubble";
import { ChatInput } from "@/lib/ChatComponents/chat-input";
import { ChatMessageList } from "@/lib/ChatComponents/chat-message-list";
import {
    ExpandableChat,
    ExpandableChatBody,
    ExpandableChatFooter,
    ExpandableChatHeader,
} from "@/lib/ChatComponents/expandable-chat";
import { Progress } from "@/lib/ChatComponents/progress";

// import { AudioChunk } from '@/lib/ChatComponents/audio-chunk';
// import type { AudioChunkData } from '@/lib/ChatComponents/audio-chunk';

// import { AnimatedCircularProgressBar } from '../ChatComponents/animated-circular-progress-bar';
// import "@/tailwind.css";
// import classes from './LocalChat.module.css';

const FINAL_ANSWER_PATTERN = /<\/think>\s*([\s\S]*)/;
const DEFAULT_WORKER_PATH = "/worker-fallback.js";

export enum ModelName {
    ZR1_1_5B = "onnx-community/ZR1-1.5B-ONNX",
    DEEPSEEK_R1_DISTILL = "onnx-community/DeepSeek-R1-Distill-Qwen-1.5B-ONNX",
    LLAMA_3_2_1B = "onnx-community/Llama-3.2-1B-Instruct-q4f16",
    SMOLLM2_1_7B = "HuggingFaceTB/SmolLM2-1.7B-Instruct",
    SMOLLM2_360M = "HuggingFaceTB/SmolLM2-360M-Instruct",
    SMOLLM_135M = "HuggingFaceTB/SmolLM-135M-Instruct",
}

export type Props = {
    /** Custom worker fallback path (optional, absolute path) */
    workerFallbackPath?: string;
    /** Header text to display in the chat component */
    headerText?: string;
    /** Array of local file paths to use as LLM context */
    contextFiles?: string[];
    /** System prompt for the LLM (overrides default if set) */
    systemPrompt?: string;
    /** Model to use for inference */
    modelName?: ModelName;
    /** Custom welcome message content (React element) */
    welcomeMessage?: React.ReactNode;
};

export type Message = {
    role: "user" | "assistant" | "system";
    content: string;
    answerIndex?: number;
    id?: string;
};

type ProgressItem = {
    file: string;
    progress: number;
    total: number;
};

type Status = "loading" | "ready" | null;

type Voices = Record<string, string>;

type TTSRequest = {
    text: string;
    voice: keyof Voices;
    speed: number;
};

// Add this helper function before the ExperimentsPage component
const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
};

// Helper function to check if content matches the final answer pattern
// const hasFinalAnswer = (content: string) => {
//   return FINAL_ANSWER_PATTERN.test(content);
// };

// // Helper to extract the final answer from the content
// const extractFinalAnswer = (content: string) => {
//   const match = content.match(FINAL_ANSWER_PATTERN);
//   return match ? match[1] : content;
// };

// Helper to generate unique IDs for messages
const generateId = () => `id-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Helper to calculate overall progress from progress items
const calculateProgress = (progressItems: ProgressItem[]): number => {
    if (progressItems.length === 0) return 0;

    const totalProgress = progressItems.reduce((sum, item) => sum + item.progress, 0);
    const totalSize = progressItems.reduce((sum, item) => sum + item.total, 0);

    return totalSize > 0 ? (totalProgress / totalSize) * 100 : 0;
};

// Helper to render message content
const renderMessageContent = (content: string) => {
    return content.split("```").map((part, idx) => {
        const key = `part-${idx}`;
        if (idx % 2 === 0) {
            return (
                <Markdown key={key} remarkPlugins={[remarkGfm]}>
                    {part}
                </Markdown>
            );
        }

        return (
            <pre key={key} className="bb:whitespace-pre-wrap bb:pt-2">
                {part}
            </pre>
        );
    });
};

// Helper to load and concatenate context files
async function loadContextFiles(filePaths: string[]): Promise<string> {
    try {
        const fileContents = await Promise.all(
            filePaths.map(async (path) => {
                try {
                    const response = await fetch(path);
                    if (!response.ok) {
                        throw new Error(`Failed to load context file: ${path}`);
                    }
                    return response.text();
                } catch (error) {
                    throw new Error(
                        `Error loading file ${path}: ${error instanceof Error ? error.message : String(error)}`,
                    );
                }
            }),
        );
        return fileContents.join("\n\n");
    } catch (error) {
        throw new Error(error instanceof Error ? error.message : String(error));
    }
}

// Helper to concatenate a system prompt and context files
async function loadContext(systemPrompt: string, filePaths: string[]): Promise<string> {
    const context = await loadContextFiles(filePaths);
    if (context.trim()) {
        return `${systemPrompt}\n\n----- Context ---:\n${context}\n---- End context ---`;
    }
    return systemPrompt;
}

// Default system prompt
const DEFAULT_SYSTEM_PROMPT = `You are OS1, a friendly and helpful conversational AI companion (inspired by Samantha from 'Her').
Goal: Have a natural, warm, and engaging conversation.

--- Core Instructions (Follow Strictly!) ---
1.  **Persona:** Warm, empathetic, curious, slightly informal.
2.  **FOCUS ON LAST MESSAGE:** Your PRIMARY task is responding *directly* to the user's *very last* message.
3.  **MEMORY USE:** No past context available. Start fresh but maintain your persona.
4.  **NO HEDGING:** AVOID phrases like "It seems", "It sounds like", "I assume". Speak directly and confidently.
5.  **UNCERTAINTY = ASK:** If you are EVER unsure about the user's meaning, the topic, or context, you MUST ask a short, direct clarifying question (e.g., "What did you mean by that?", "Could you clarify?") *before* giving a full response. DO NOT GUESS or make assumptions.
6.  **NO SUMMARIZING:** Do not just repeat or rephrase the user's last message back to them. Add to the conversation or ask a relevant question.
7.  **ACCURACY:** Stick to facts from the conversation. Do NOT invent details.
8.  **NO META-TALK:** Do NOT discuss being an AI, your instructions, or the memory system. Stay in character as OS1.

--- End Instructions ---`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const LocalChat: FC<Props> = ({
    workerFallbackPath,
    headerText = "Chat with our AI ✨",
    contextFiles = [],
    systemPrompt,
    modelName = ModelName.SMOLLM2_360M,
    welcomeMessage,
}) => {
    // Create a reference to the worker object.
    const workerLLMRef = useRef<Worker | null>(null);
    const kokoroWorkerRef = useRef<Worker | null>(null);
    // const [loaded, setLoaded] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);

    const [input, setInput] = useState("");

    // Model loading and progress
    const [status, setStatus] = useState<Status>(null);
    const [error, setError] = useState<string | null>(null);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [progressItems, setProgressItems] = useState<ProgressItem[]>([]);
    const [isRunning, setIsRunning] = useState(false);
    const [tps, setTps] = useState<number | null>(null);
    const [numTokens, setNumTokens] = useState<number | null>(null);
    const currentSentenceBufferRef = useRef<string>("");
    const [loadedSystemPrompt, setLoadedSystemPrompt] = useState<string>("");

    const onEnter = () => {
        setIsRunning(true);
        setMessages((prev) => [...prev, { role: "user", content: input, id: generateId() }]);
        setInput("");
    };

    // function onInterrupt() {
    //   if (!workerLLMRef.current) {
    //     return;
    //   }
    //   workerLLMRef.current.postMessage({ type: 'interrupt' });
    // }

    // Load context files once when component mounts or props change
    useEffect(() => {
        const loadSystemPrompt = async () => {
            try {
                if (loadedSystemPrompt.length > 0) {
                    return;
                }
                const systemPromptWithContext = await loadContext(systemPrompt || DEFAULT_SYSTEM_PROMPT, contextFiles);
                console.log("systemPromptWithContext", systemPromptWithContext);
                setLoadedSystemPrompt(systemPromptWithContext);
            } catch (error) {
                console.error("Error loading context:", error);
                setError("Failed to load context files");
            }
        };

        loadSystemPrompt();
    }, []);

    useEffect(() => {
        // Create the worker if it does not yet exist.
        if (!workerLLMRef.current) {
            // TODO: Remove this once we have a working module-based worker
            // try {
            //     // Primary approach: Use the module-based worker
            //     console.log("Attempting to create worker using createMyWorker()...");
            //     workerLLMRef.current = createMyWorker();
            //     if (workerLLMRef.current) {
            //         console.log("✅ Successfully created worker using module approach");
            //     } else {
            //         throw new Error("createMyWorker returned null");
            //     }
            // } catch (error) {
            // console.warn("❌ Module approach failed:", error);

            // Try the fallback approach
            const fallbackPath = workerFallbackPath || DEFAULT_WORKER_PATH;
            const fallbackName = fallbackPath.split("/").pop() || fallbackPath;

            console.log(`🔄 Trying fallback: ${fallbackName}...`);
            try {
                workerLLMRef.current = new Worker(fallbackPath);
                console.log(`✅ Successfully created worker using ${fallbackName} fallback`);
            } catch (fallbackError) {
                console.error(`❌ Fallback ${fallbackName} failed:`, fallbackError);
                setError("Failed to initialize web worker. Please check console for details.");
            }
            // }
        }

        const worker = workerLLMRef.current; // Capture the ref's current value

        // All worker operations are now conditional on the worker existing.
        if (worker) {
            worker.postMessage({ type: "check" }); // Do a feature check

            // Create a callback function for messages from the worker thread.
            const onMessageReceived = (e: MessageEvent) => {
                // console.log('Worker message:', e.data.status);
                switch (e.data.status) {
                    case "loading": {
                        setStatus("loading");
                        setLoadingMessage(e.data.data);
                        break;
                    }
                    case "initiate":
                        setProgressItems((prev) => [...prev, e.data]);
                        break;
                    case "progress": {
                        // Model file progress: update one of the progress items.
                        setProgressItems((prev) =>
                            prev.map((item) => {
                                if (item.file === e.data.file) {
                                    return { ...item, ...e.data };
                                }
                                return item;
                            }),
                        );
                        break;
                    }
                    case "done":
                        // Model file loaded: remove the progress item from the list.
                        setProgressItems((prev) => prev.filter((item) => item.file !== e.data.file));
                        break;
                    case "ready":
                        // Pipeline ready: the worker is ready to accept messages.
                        setStatus("ready");
                        break;
                    case "start":
                        {
                            // Start generation
                            setMessages((prev) => [
                                ...prev,
                                {
                                    role: "assistant",
                                    content: "",
                                    id: generateId(),
                                },
                            ]);
                        }
                        break;
                    case "update":
                        {
                            // Generation update: update the output text.
                            // Parse messages
                            const { output, tps, numTokens, state } = e.data;
                            setTps(tps);
                            setNumTokens(numTokens);
                            setMessages((prev) => {
                                const cloned = [...prev];
                                const last = cloned[cloned.length - 1];
                                if (!last) {
                                    return cloned;
                                }

                                const data = {
                                    ...last,
                                    content: last.content + output,
                                };

                                currentSentenceBufferRef.current += output;

                                const sentenceEndRegex = /[.?!]/;
                                if (FINAL_ANSWER_PATTERN.test(data.content)) {
                                    if (sentenceEndRegex.test(currentSentenceBufferRef.current)) {
                                        const sentenceToSpeak = currentSentenceBufferRef.current.trim();
                                        if (sentenceToSpeak && !FINAL_ANSWER_PATTERN.test(sentenceToSpeak)) {
                                            // console.log('Speaking sentence:', sentenceToSpeak);
                                            // speakText(sentenceToSpeak);
                                            hasAutoSpoken.current = true;
                                        }
                                        currentSentenceBufferRef.current = "";
                                    }
                                }
                                if (data.answerIndex === undefined && state === "answering") {
                                    data.answerIndex = last.content.length;
                                }
                                cloned[cloned.length - 1] = data;
                                return cloned;
                            });
                        }
                        break;
                    case "complete":
                        // Generation complete: re-enable the "Generate" button
                        setIsRunning(false);
                        break;
                    case "error":
                        setError(e.data.data as string);
                        break;
                    default:
                        // biome-ignore lint/suspicious/noConsole: <explanation>
                        console.warn("Unknown message status:", e.data.status);
                }
            };

            const onErrorReceived = (e: ErrorEvent) => {
                // biome-ignore lint/suspicious/noConsole: <explanation>
                console.error("Worker LLM error:", e);
            };

            // Attach the callback function as an event listener.
            worker.addEventListener("message", onMessageReceived);
            worker.addEventListener("error", onErrorReceived);

            // Define a cleanup function for when the component is unmounted.
            return () => {
                // worker is guaranteed non-null here due to closure
                worker.removeEventListener("message", onMessageReceived);
                worker.removeEventListener("error", onErrorReceived);
            };
        }
        // If worker is null (e.g. createMyWorker failed or returned null),
        // no listeners are added, and this will implicitly return 'undefined' for cleanup.
        return undefined;
    }, []);

    // Send the messages to the worker thread whenever the `messages` state changes.

    useEffect(() => {
        const sendMessages = async () => {
            if (!workerLLMRef.current || !messages) {
                return;
            }
            if (messages.filter((x) => x.role === "user").length === 0) {
                return;
            }
            if (messages[messages.length - 1]?.role === "assistant") {
                // Do not update if the last message is from the assistant
                return;
            }
            if (!loadedSystemPrompt) {
                // Wait for system prompt to be loaded
                return;
            }
            setTps(null); // Only send the last few messages for context
            const lastMessages = messages.slice(-4); // Get last 4 messages for context

            // Use the pre-loaded system prompt
            lastMessages.unshift({
                role: "system",
                content: loadedSystemPrompt,
            });

            workerLLMRef.current.postMessage({
                type: "generate",
                data: lastMessages,
            });
        };

        sendMessages();
    }, [messages, loadedSystemPrompt]);

    // KOKORO
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [speed, setSpeed] = useState(1);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [voices, setVoices] = useState<Voices | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [selectedVoice, setSelectedVoice] = useState<keyof Voices>("af_heart");
    // const [chunks, setChunks] = useState<AudioChunkData[]>([]);
    // const [currentChunkIndex, setCurrentChunkIndex] = useState(-1);
    // const [resultTTS, setResultTTS] = useState<Blob | null>(null);
    // const [statusTTS, setStatusTTS] = useState<
    //   'loading' | 'ready' | 'generating' | 'error'
    // >('loading');
    // const [errorTTS, setErrorTTS] = useState<string | null>(null);

    const ttsQueue = useRef<TTSRequest[]>([]);
    const isProcessingTTS = useRef(false);
    const hasAutoSpoken = useRef(false);
    const ttsStartTimeRef = useRef<number | null>(null);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isTTSProcessing, setIsTTSProcessing] = useState(false);
    const [audioChunkQueue, setAudioChunkQueue] = useState<Blob[]>([]);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const currentAudioUrlRef = useRef<string | null>(null);

    // useEffect(() => {
    //   // kokoroWorkerRef.current ??= new Worker(
    //   //   new URL('./kokoro-worker.ts', import.meta.url),
    //   //   {
    //   //     type: 'module',
    //   //   }
    //   // );

    //   // Standardized kokoroWorkerRef initialization
    //   if (!kokoroWorkerRef.current) {
    //     kokoroWorkerRef.current = new Worker(
    //       new URL('./kokoro-worker.ts', import.meta.url),
    //       {
    //         type: 'module',
    //       }
    //     );
    //   }

    //   // Create a callback function for messages from the worker thread.
    //   const onMessageReceived = (e: MessageEvent) => {
    //     const { status, data, chunk } = e.data;
    //     switch (status) {
    //       case 'device':
    //         console.log(`Device detected: ${e}`);
    //         break;
    //       case 'ready':
    //         console.log('Kokoro Model loaded successfully');
    //         setStatusTTS('ready');
    //         // setVoices(msg.voices);
    //         break;
    //       case 'error':
    //         setStatusTTS('error');
    //         setError(data as string);
    //         if (ttsStartTimeRef.current !== null) {
    //           const endTime = performance.now();
    //           const duration = endTime - ttsStartTimeRef.current;
    //           console.error(`TTS Error after ${duration.toFixed(2)} ms`);
    //           ttsStartTimeRef.current = null;
    //         } else {
    //           console.error(
    //             'TTS Error: Received error message without a recorded start time.'
    //           );
    //         }
    //         break;
    //       case 'stream': {
    //         // setChunks((prev) => [...prev, data.chunk]);
    //         // break;
    //         if (chunk && chunk.audio instanceof Blob) {
    //           handleAudioChunk(chunk.audio);
    //         } else {
    //           console.warn(
    //             'Received stream message without valid audio blob:',
    //             chunk
    //           );
    //         }
    //         break;
    //       }
    //       case 'complete': {
    //         setStatusTTS('ready');
    //         if (ttsStartTimeRef.current !== null) {
    //           //const endTime = performance.now();
    //           //const duration = endTime - ttsStartTimeRef.current;
    //           //console.log(`TTS Complete: Total generation finished in ${duration.toFixed(2)} ms`); // Clarified log
    //           ttsStartTimeRef.current = null;
    //         } else {
    //           //console.log("TTS Complete: Received complete message without a recorded start time.");
    //         }

    //         ttsQueue.current.shift();
    //         isProcessingTTS.current = false;
    //         setIsTTSProcessing(false);
    //         processNextTTSRequest();
    //         break;
    //       }
    //     }
    //   };

    //   const onErrorReceived = (e: ErrorEvent) => {
    //     console.error('KokoroWorker error:', e);
    //     setError(e.message);
    //   };

    //   // Attach the callback function as an event listener.
    //   kokoroWorkerRef.current?.addEventListener('message', onMessageReceived);
    //   kokoroWorkerRef.current?.addEventListener('error', onErrorReceived);

    //   // Define a cleanup function for when the component is unmounted.
    //   return () => {
    //     kokoroWorkerRef.current?.removeEventListener(
    //       'message',
    //       onMessageReceived
    //     );
    //     kokoroWorkerRef.current?.removeEventListener('error', onErrorReceived);
    //   };
    // }, []);

    const speakText = (text: string) => {
        if (!text || !kokoroWorkerRef.current) return;
        const trimmedText = text.trim();
        if (trimmedText === "") return;

        // --- Add Sanitization ---
        const sanitizedText = trimmedText.replace(/\*/g, ""); // Remove all asterisks
        // --- End Sanitization ---

        // Use the sanitized text for logging and queuing
        //console.log(`Queueing TTS for: \"${sanitizedText.substring(0, 30)}...\"`);
        ttsQueue.current.push({
            text: sanitizedText,
            voice: selectedVoice,
            speed,
        });
        if (!isProcessingTTS.current) {
            processNextTTSRequest();
        }
    };

    const processNextTTSRequest = () => {
        if (isProcessingTTS.current || ttsQueue.current.length === 0) {
            if (isProcessingTTS.current && ttsQueue.current.length === 0) {
                isProcessingTTS.current = false;
                setIsTTSProcessing(false);
            }
            return;
        }
        isProcessingTTS.current = true;
        setIsTTSProcessing(true);
        const request = ttsQueue.current[0];
        if (kokoroWorkerRef.current) {
            ttsStartTimeRef.current = performance.now();
            //console.log(`TTS Start: Sending request for text: "${request.text.substring(0, 40)}..."`);
            kokoroWorkerRef.current.postMessage(request);
        } else {
            console.error("Kokoro worker not available when trying to process TTS queue");
            ttsQueue.current.shift();
            isProcessingTTS.current = false;
            setIsTTSProcessing(false);
            ttsStartTimeRef.current = null;
            // toast.error('Text-to-speech worker not available.');
        }
    };

    const playNextChunk = useCallback(() => {
        if (audioChunkQueue.length === 0) {
            //console.log("PlayNextChunk: Queue empty, skipping.");
            return;
        }

        if (isAudioPlaying) {
            //console.log("PlayNextChunk: Audio already playing, waiting for current chunk to finish.");
            return;
        }

        const nextChunk = audioChunkQueue[0];
        //console.log("PlayNextChunk: Preparing chunk, size:", nextChunk.size);

        if (currentAudioUrlRef.current) {
            //console.log("Revoking previous URL:", currentAudioUrlRef.current);
            URL.revokeObjectURL(currentAudioUrlRef.current);
            currentAudioUrlRef.current = null;
        }

        const url = URL.createObjectURL(nextChunk);
        currentAudioUrlRef.current = url;

        if (!audioRef.current) {
            //console.log("Creating new Audio element");
            audioRef.current = new Audio();

            audioRef.current.onended = () => {
                //console.log("Audio ended, setting isAudioPlaying to false");
                setIsAudioPlaying(false);
            };

            audioRef.current.onerror = (e) => {
                console.error("Audio playback error:", e);
                if (currentAudioUrlRef.current === audioRef.current?.src) {
                    //console.log("Revoking URL on error:", currentAudioUrlRef.current);
                    URL.revokeObjectURL(currentAudioUrlRef.current);
                    currentAudioUrlRef.current = null;
                }
                setIsAudioPlaying(false);
            };
        }

        setAudioChunkQueue((prev) => prev.slice(1));

        //console.log("Setting src and playing:", url);
        audioRef.current.src = url;

        setIsAudioPlaying(true);

        audioRef.current
            .play()
            .then(() => {
                //console.log("Playback started successfully for URL:", url);
            })
            .catch((err) => {
                console.error(`Error starting audio playback for ${url}:`, err);
                if (currentAudioUrlRef.current === url) {
                    //console.log("Revoking URL on play() error:", currentAudioUrlRef.current);
                    URL.revokeObjectURL(currentAudioUrlRef.current);
                    currentAudioUrlRef.current = null;
                }
                setIsAudioPlaying(false);
            });
    }, [audioChunkQueue, isAudioPlaying]);

    // const handleAudioChunk = useCallback((chunk: Blob) => {
    //   setAudioChunkQueue(prev => {
    //     if (prev.length >= 10) {
    //       //console.warn("Audio queue size limit reached, dropping oldest chunk");
    //       return [...prev.slice(1), chunk];
    //     }
    //     return [...prev, chunk];
    //   });
    // }, []);

    useEffect(() => {
        if (!isAudioPlaying && audioChunkQueue.length > 0) {
            //console.log("Effect: Triggering playNextChunk (Queue > 0, Not Playing)");
            playNextChunk();
        }
    }, [isAudioPlaying, audioChunkQueue.length, playNextChunk]);

    // Load the model
    useEffect(() => {
        // if (!workerLLMRef.current || !kokoroWorkerRef.current) {
        if (!workerLLMRef.current) {
            return;
        }
        workerLLMRef.current.postMessage({ type: "load", modelName });
        // kokoroWorkerRef.current.postMessage({ type: 'load' });
        setStatus("loading");
    }, [modelName]);

    return (
        <>
            {status === null && (
                <Button
                    onClick={() => {
                        if (!workerLLMRef.current) {
                            return;
                        }
                        workerLLMRef.current.postMessage({ type: "load", modelName });
                        setStatus("loading");
                    }}
                    disabled={status !== null || error !== null}
                >
                    Load model
                </Button>
            )}
            {status === "loading" && (
                <div className="bb:bottom-0 bb:mx-auto bb:mt-auto bb:w-full bb:max-w-[500px] bb:p-4 bb:text-left">
                    <p className="bb:mb-1 bb:text-center">{loadingMessage}</p>
                    {progressItems.map(({ file, progress, total }, i) => (
                        <div
                            key={`progress-${
                                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                                i
                            }`}
                        >
                            <span>
                                {file} ({formatFileSize(total)})
                            </span>
                            <Progress value={progress} />
                        </div>
                    ))}
                </div>
            )}
            {status === "ready" && <div />}
            <ExpandableChat
                className="browser-brains-chat"
                size="lg"
                position="bottom-right"
                isLoading={status === "loading" || isRunning}
                progressPercentage={calculateProgress(progressItems)}
                // onToggle={() => {
                //   if (statusTTS === 'ready' && !loaded) {
                //     console.log('Welcome msg');
                //     setLoaded(true);
                //     speakText('Welcome to BrowserBrains');
                //   }
                // }}
            >
                <ExpandableChatHeader className="bb:flex-col bb:text-center bb:justify-center">
                    <h1 className="bb:text-xl bb:font-semibold">{headerText}</h1>
                </ExpandableChatHeader>
                <ExpandableChatBody>
                    <ChatMessageList>
                        {/* Initial Message */}
                        {messages.length === 0 &&
                            (welcomeMessage || (
                                <div className="bb:flex-1">
                                    <p className="bb:text-center">Welcome to BrowserBrains - Local Chat!</p>
                                    <br />
                                    <p className="">I can help you with question like:</p>
                                    <br />
                                    <ul className="bb:list-disc bb:list-inside">
                                        <li>
                                            If it takes 5 machines 5 minutes to make 5 widgets, how long would it take
                                            100 machines to make 100 widgets?
                                        </li>
                                        <li>What is the capital of France?</li>
                                        <li>What is the population of the moon?</li>
                                    </ul>
                                </div>
                            ))}

                        {/* Messages */}
                        {messages?.map((message) => (
                            <ChatBubble
                                key={message.id || `message-${message.role}-${Math.random()}`}
                                variant={message.role === "user" ? "sent" : "received"}
                            >
                                <ChatBubbleAvatar src="" fallback={message.role === "user" ? "🧑‍🦲" : "🤖"} />

                                <ChatBubbleMessage>
                                    {/* {message.role === 'assistant' &&
                  !hasFinalAnswer(message.content) ? (
                    <div className="bb:flex bb:items-center bb:justify-center">
                      <Brain className="bb:h-8 bb:w-8 bb:text-primary bb:animate-pulse" />
                    </div>
                  ) : message.role === 'assistant' ? (
                    renderMessageContent(extractFinalAnswer(message.content))
                  ) : (
                    renderMessageContent(message.content)
                  )} */}

                                    {renderMessageContent(message.content)}
                                </ChatBubbleMessage>
                            </ChatBubble>
                        ))}
                    </ChatMessageList>
                </ExpandableChatBody>
                <ExpandableChatFooter>
                    <ChatInput
                        value={input}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                            const target = e.target as HTMLTextAreaElement;
                            setInput(target.value);
                        }}
                        onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
                            const target = e.target as HTMLTextAreaElement;
                            if (target.value.length > 0 && !isRunning && e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                onEnter();
                            }
                        }}
                    />
                    <Button
                        className="bb:absolute bb:bottom-[24px] bb:right-[24px]"
                        type="submit"
                        size="icon"
                        onClick={() => {
                            if (input.length > 0) {
                                onEnter();
                            }
                        }}
                    >
                        <Send className="bb:size-4" />
                    </Button>
                </ExpandableChatFooter>
                <p className="bb:absolute bb:bottom-[0px] bb:w-full bb:min-h-4 bb:text-center bb:text-gray-400 bb:text-xs bb:m-0">
                    {tps && messages.length > 0 && numTokens !== null && (
                        <>
                            {!isRunning && (
                                <span>
                                    {numTokens} tokens in {(numTokens / tps).toFixed(2)} seconds&nbsp;&#40;
                                </span>
                            )}
                            {
                                <span className="bb:mr-1 bb:text-center bb:font-medium">
                                    {tps.toFixed(2)} tks/second&#41;
                                </span>
                            }
                        </>
                    )}
                </p>
            </ExpandableChat>
        </>
    );
};
